<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Button from '../../Button.svelte';
  
  export let isOpen = false;
  export let employerName = '';
  export let isUnarchive = false; // true for unarchive, false for archive
  
  const dispatch = createEventDispatcher();
  
  function handleConfirm() {
    dispatch('confirm');
    isOpen = false;
  }
  
  function handleCancel() {
    dispatch('cancel');
    isOpen = false;
  }
  
  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleCancel();
    }
  }
</script>

{#if isOpen}
  <div class="modal-backdrop" role="presentation" on:click|self={handleCancel} on:keydown={(e) => e.key === 'Escape' && handleCancel()}>
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="archive-employer-title">
      <div class="modal-header">
        <div class="warning-icon">
          {#if isUnarchive}
            <!-- Unarchive icon -->
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12h-8m0 0V4m0 8l7-7m-7 7l7 7"/>
              <circle cx="4" cy="12" r="2"/>
            </svg>
          {:else}
            <!-- Archive icon -->
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18"></path>
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
              <polyline points="10,11 10,17"></polyline>
              <polyline points="14,11 14,17"></polyline>
            </svg>
          {/if}
        </div>
        <h2 id="archive-employer-title" class="modal-title">{isUnarchive ? 'Unarchive' : 'Archive'} Employer</h2>
      </div>
      
      <div class="modal-body">
        <p class="confirmation-text">
          Are you sure you want to {isUnarchive ? 'unarchive' : 'archive'} <strong>{employerName}</strong>?
        </p>
        <p class="warning-text">
          {#if isUnarchive}
            This action will restore the employer to your active list.
          {:else}
            This action will remove the employer from your active list.
          {/if}
        </p>
      </div>
      
      <div class="modal-footer">
        <button 
          class="cancel-button"
          on:click={handleCancel}
        >
          Cancel
        </button>
        <button 
          class="archive-confirm-button"
          on:click={handleConfirm}
        >
          {#if isUnarchive}
            <!-- Unarchive icon -->
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12h-8m0 0V4m0 8l7-7m-7 7l7 7"/>
              <circle cx="4" cy="12" r="2"/>
            </svg>
            Unarchive Employer
          {:else}
            <!-- Archive icon -->
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18"></path>
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
              <polyline points="10,11 10,17"></polyline>
              <polyline points="14,11 14,17"></polyline>
            </svg>
            Archive Employer
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(8px);
  }
  
  .modal-container {
    background: linear-gradient(135deg, #ffffff, #f8fafc);
    border-radius: 20px;
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    max-width: 440px;
    width: 90%;
    max-height: 90vh;
    overflow: hidden;
    animation: modalSlideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  .modal-header {
    border-bottom: 0;
    padding: 2rem 2rem 0 2rem;
    display: flex;
    align-items: center;
    gap: 1.25rem;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.5));
  }
  
  .warning-icon {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #fef2f2, #fee2e2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #dc2626;
    flex-shrink: 0;
    box-shadow: 
      0 8px 16px rgba(220, 38, 38, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(220, 38, 38, 0.1);
  }
  
  .modal-title {
    font-size: 1.375rem;
    font-weight: 700;
    background: linear-gradient(135deg, #111827, #374151);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .modal-body {
    padding: 1.5rem 2rem;
    background: rgba(255, 255, 255, 0.5);
  }
  
  .confirmation-text {
    font-size: 1rem;
    color: #374151;
    margin: 0 0 1rem 0;
    line-height: 1.5;
  }
  
  .confirmation-text strong {
    color: #111827;
    font-weight: 600;
  }
  
  .warning-text {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }
  
  .modal-footer {
    border-top: 0;
    padding: 0 2rem 2rem 2rem;
    margin-bottom: 1rem;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.5));
  }
  
  .cancel-button,
  .archive-confirm-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: none;
    flex: 1;
    min-height: 48px;
  }

  .cancel-button {
    color: #374151;
    background: linear-gradient(135deg, #f9fafb, #f3f4f6);
    border: 1px solid #e5e7eb;
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .cancel-button:hover {
    background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
    transform: translateY(-2px);
    box-shadow: 
      0 8px 15px -3px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }
  
  .archive-confirm-button {
    color: white;
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    box-shadow: 
      0 4px 6px -1px rgba(220, 38, 38, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  
  .archive-confirm-button:hover {
    background: linear-gradient(135deg, #b91c1c, #991b1b);
    transform: translateY(-2px);
    box-shadow: 
      0 8px 15px -3px rgba(220, 38, 38, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  
  .cancel-button:active,
  .archive-confirm-button:active {
    transform: translateY(0);
  }
  
  @media (max-width: 640px) {
    .modal-container {
      margin: 1rem;
      width: calc(100% - 2rem);
      border-radius: 16px;
    }
    
    .modal-header {
      padding: 1.5rem 1.5rem 0 1.5rem;
    }
    
    .modal-body {
      padding: 1.5rem;
    }
    
    .modal-footer {
      padding: 0 1.5rem 1.5rem 1.5rem;
      flex-direction: column-reverse;
      gap: 0.75rem;
    }
    
    .cancel-button,
    .archive-confirm-button {
      width: 100%;
      flex: none;
    }
  }
</style> 